import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Plus, Search, Power, Pencil } from "lucide-react";

export default function PaymentTerms() {
  const { user } = useGoogleAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emitenteIdFiltro, setEmitenteIdFiltro] = useState<number | undefined>(undefined);
  
  // Form states
  const [descricao, setDescricao] = useState("");
  const [prazoPrimeiraParcela, setPrazoPrimeiraParcela] = useState("");
  const [prazoEntreParcelas, setPrazoEntreParcelas] = useState("");
  const [quantidadeParcelas, setQuantidadeParcelas] = useState("");
  const [prazosParcelas, setPrazosParcelas] = useState("");
  const [formEmitenteId, setFormEmitenteId] = useState<number | null>(null);
  const [editingCondicao, setEditingCondicao] = useState<any>(null);

  // Query de emitentes (admin: todos; cliente: emitente e subemitentes vinculados)
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !!user,
  });

  // Query
  const { data: condicoes = [], refetch } = trpc.paymentTerms.list.useQuery(
    emitenteIdFiltro ? { emitenteId: emitenteIdFiltro } : undefined,
    { enabled: !!user }
  );
  
  // Mutations
  const createMutation = trpc.paymentTerms.create.useMutation({
    onSuccess: () => {
      toast.success("Condição de pagamento criada com sucesso");
      handleCloseModal();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar condição de pagamento");
    },
  });

  const updateMutation = trpc.paymentTerms.update.useMutation({
    onSuccess: () => {
      toast.success("Condição de pagamento atualizada com sucesso");
      handleCloseModal();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar condição de pagamento");
    },
  });
  
  const toggleAtivoMutation = trpc.paymentTerms.toggleAtivo.useMutation({
    onSuccess: () => {
      toast.success("Status atualizado com sucesso");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar status");
    },
  });
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDescricao("");
    setPrazoPrimeiraParcela("");
    setPrazoEntreParcelas("");
    setQuantidadeParcelas("");
    setPrazosParcelas("");
    setFormEmitenteId(null);
    setEditingCondicao(null);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const normalizarPrazosParcelas = (valor: unknown): number[] => {
    if (Array.isArray(valor)) return valor.map(Number).filter((prazo) => Number.isInteger(prazo) && prazo >= 0);
    if (!valor) return [];
    try {
      const parsed = JSON.parse(String(valor));
      return Array.isArray(parsed) ? parsed.map(Number).filter((prazo) => Number.isInteger(prazo) && prazo >= 0) : [];
    } catch {
      return [];
    }
  };

  const handleOpenEditModal = (condicao: any) => {
    setEditingCondicao(condicao);
    setDescricao(condicao.descricao);
    setPrazoPrimeiraParcela(String(condicao.prazoPrimeiraParcela));
    setPrazoEntreParcelas(String(condicao.prazoEntreParcelas));
    setQuantidadeParcelas(String(condicao.quantidadeParcelas));
    setPrazosParcelas(normalizarPrazosParcelas(condicao.prazosParcelas).join(", "));
    setFormEmitenteId(condicao.emitenteId ?? null);
    setIsModalOpen(true);
  };

  const handlePrazosParcelasChange = (valor: string) => {
    setPrazosParcelas(valor);
    const valores = valor.split(",").map((prazo) => prazo.trim());
    if (!valor.trim() || valores.some((prazo) => !prazo || !Number.isInteger(Number(prazo)) || Number(prazo) < 0)) return;
    setPrazoPrimeiraParcela(valores[0]);
    setPrazoEntreParcelas("0");
    setQuantidadeParcelas(String(valores.length));
  };
  
  const handleSubmit = () => {
    const textoPrazosParcelas = prazosParcelas.trim();
    let prazosParcelasNumericos: number[] | null = null;
    if (textoPrazosParcelas) {
      const valores = textoPrazosParcelas.split(",").map((prazo) => prazo.trim());
      if (valores.some((prazo) => !prazo || !Number.isInteger(Number(prazo)) || Number(prazo) < 0)) {
        toast.error("Prazos personalizados devem conter somente dias inteiros não negativos, separados por vírgula");
        return;
      }
      prazosParcelasNumericos = valores.map(Number);
    }
    if (!descricao.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }
    if (!prazoPrimeiraParcela || parseInt(prazoPrimeiraParcela) < 0) {
      toast.error("Prazo da primeira parcela inválido");
      return;
    }
    if (!prazoEntreParcelas || parseInt(prazoEntreParcelas) < 0) {
      toast.error("Prazo entre parcelas inválido");
      return;
    }
    if (!quantidadeParcelas || parseInt(quantidadeParcelas) < 1) {
      toast.error("Quantidade de parcelas deve ser maior que zero");
      return;
    }

    const dados = {
      emitenteId: formEmitenteId,
      descricao: descricao.trim(),
      prazoPrimeiraParcela: parseInt(prazoPrimeiraParcela),
      prazoEntreParcelas: parseInt(prazoEntreParcelas),
      quantidadeParcelas: parseInt(quantidadeParcelas),
      ...(prazosParcelasNumericos
        ? { prazosParcelas: prazosParcelasNumericos }
        : editingCondicao && normalizarPrazosParcelas(editingCondicao.prazosParcelas).length > 0
          ? { prazosParcelas: null }
          : {}),
    };

    if (editingCondicao) {
      updateMutation.mutate({ id: editingCondicao.id, ...dados });
    } else {
      createMutation.mutate(dados);
    }
  };
  
  const calculateDescritivo = (primeira: number, entre: number, qtd: number) => {
    const prazos: number[] = [];
    for (let i = 0; i < qtd; i++) {
      if (i === 0) {
        prazos.push(primeira);
      } else {
        prazos.push(primeira + (entre * i));
      }
    }
    return prazos.join(", ");
  };

  const calculateDescritivoCondicao = (condicao: any) => {
    const personalizados = normalizarPrazosParcelas(condicao.prazosParcelas);
    return personalizados.length > 0
      ? personalizados.join(", ")
      : calculateDescritivo(condicao.prazoPrimeiraParcela, condicao.prazoEntreParcelas, condicao.quantidadeParcelas);
  };
  
  const filteredCondicoes = condicoes.filter((c: any) =>
    c.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Condições de Pagamento</CardTitle>
            <Button onClick={handleOpenModal}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtro */}
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {user?.role !== "cliente" && (
              <Select
                value={emitenteIdFiltro ? String(emitenteIdFiltro) : "todos"}
                onValueChange={(value) => setEmitenteIdFiltro(value === "todos" ? undefined : Number(value))}
              >
                <SelectTrigger className="sm:w-[260px]">
                  <SelectValue placeholder="Todos os emitentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os emitentes</SelectItem>
                  {emitentes.map((emitente: any) => (
                    <SelectItem key={emitente.id} value={String(emitente.id)}>
                      {emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {/* Tabela */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 font-medium">ID</th>
                  <th className="text-left p-3 font-medium">Descrição</th>
                  <th className="text-left p-3 font-medium">Emitente</th>
                  <th className="text-center p-3 font-medium">Prazo 1ª Parcela (dias)</th>
                  <th className="text-center p-3 font-medium">Prazo Entre Parcelas (dias)</th>
                  <th className="text-center p-3 font-medium">Qtd Parcelas</th>
                  <th className="text-left p-3 font-medium">Descritivo de Prazo</th>
                  <th className="text-center p-3 font-medium">Status</th>
                  <th className="text-center p-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCondicoes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center p-8 text-muted-foreground">
                      {searchTerm ? "Nenhuma condição encontrada" : "Nenhuma condição cadastrada"}
                    </td>
                  </tr>
                ) : (
                  filteredCondicoes.map((condicao: any) => (
                    <tr key={condicao.id} className="border-t hover:bg-muted/50">
                      <td className="p-3">{condicao.id}</td>
                      <td className="p-3 font-medium">{condicao.descricao}</td>
                      <td className="p-3">{condicao.emitenteRazaoSocial || condicao.emitenteNomeFantasia || `Emitente ${condicao.emitenteId}`}</td>
                      <td className="p-3 text-center">{condicao.prazoPrimeiraParcela}</td>
                      <td className="p-3 text-center">{condicao.prazoEntreParcelas}</td>
                      <td className="p-3 text-center">{condicao.quantidadeParcelas}</td>
                      <td className="p-3">
                        {calculateDescritivoCondicao(condicao)}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAtivoMutation.mutate({ id: condicao.id })}
                          className={condicao.ativo ? "text-green-600" : "text-red-600"}
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditModal(condicao)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de adicionar e editar */}
      <Dialog open={isModalOpen} onOpenChange={(open) => open ? setIsModalOpen(true) : handleCloseModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCondicao ? "Editar Condição de Pagamento" : "Nova Condição de Pagamento"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Emitente */}
            <div>
              <Label htmlFor="formEmitente">Emitente</Label>
              <Select
                value={formEmitenteId ? String(formEmitenteId) : ''}
                onValueChange={(v) => setFormEmitenteId(Number(v))}
              >
                <SelectTrigger id="formEmitente">
                  <SelectValue placeholder="Selecione um emitente" />
                </SelectTrigger>
                <SelectContent>
                  {emitentes.map((e: any) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.razaoSocial || e.nomeFantasia || `Emitente ${e.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex: 30/60/90 dias"
              />
            </div>
            
            <div>
              <Label htmlFor="prazoPrimeira">Prazo da Primeira Parcela (dias) *</Label>
              <Input
                id="prazoPrimeira"
                type="number"
                min="0"
                value={prazoPrimeiraParcela}
                onChange={(e) => setPrazoPrimeiraParcela(e.target.value)}
                placeholder="Ex: 30"
              />
            </div>
            
            <div>
              <Label htmlFor="prazoEntre">Prazo Entre Parcelas (dias) *</Label>
              <Input
                id="prazoEntre"
                type="number"
                min="0"
                value={prazoEntreParcelas}
                onChange={(e) => setPrazoEntreParcelas(e.target.value)}
                placeholder="Ex: 30"
              />
            </div>
            
            <div>
              <Label htmlFor="qtdParcelas">Quantidade de Parcelas *</Label>
              <Input
                id="qtdParcelas"
                type="number"
                min="1"
                value={quantidadeParcelas}
                onChange={(e) => setQuantidadeParcelas(e.target.value)}
                placeholder="Ex: 3"
              />
            </div>

            <div>
              <Label htmlFor="prazosParcelas">Prazos personalizados (dias, opcional)</Label>
              <Input
                id="prazosParcelas"
                value={prazosParcelas}
                onChange={(e) => handlePrazosParcelasChange(e.target.value)}
                placeholder="Ex: 30, 60, 105"
              />
              <p className="mt-1 text-xs text-muted-foreground">Quando preenchido, usa esta lista; os campos atuais ficam preenchidos apenas por compatibilidade.</p>
            </div>
            
            {/* Preview do descritivo */}
            {prazoPrimeiraParcela && prazoEntreParcelas && quantidadeParcelas && (
              <div className="bg-muted p-3 rounded-lg">
                <Label className="text-sm text-muted-foreground">Preview do Descritivo:</Label>
                <p className="font-medium mt-1">
                  {prazosParcelas.trim() || calculateDescritivo(
                    parseInt(prazoPrimeiraParcela),
                    parseInt(prazoEntreParcelas),
                    parseInt(quantidadeParcelas)
                  )}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={handleCloseModal} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending} className="flex-1">
              {createMutation.isPending || updateMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
